import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { listInvoicePaymentAllocations } from '@/app/services/voucherService';
import { PaymentAllocationResponse } from '@/app/types/payment';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PaymentAllocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: number;
  invoiceNumber: string;
  totalAmount: number;
  amountDue: number;
}

// Helper to format currency
const formatCurrency = (amount: number | string | undefined | null): string => {
  if (amount === undefined || amount === null) return '₹0.00';
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    currencyDisplay: 'symbol'
  }).format(numAmount);
};

// Helper to format date
const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return 'N/A';
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    return format(date, 'dd MMM yyyy');
  } catch (error) {
    return String(dateString);
  }
};

export const PaymentAllocationModal: React.FC<PaymentAllocationModalProps> = ({
  isOpen,
  onClose,
  invoiceId,
  invoiceNumber,
  totalAmount,
  amountDue,
}) => {

  // Fetch payment allocations for the invoice
  const { data: allocations, isLoading, error } = useQuery<PaymentAllocationResponse[]>({
    queryKey: ['invoicePaymentAllocations', invoiceId],
    queryFn: () => listInvoicePaymentAllocations(invoiceId),
    enabled: isOpen && !!invoiceId,
  });

  // Calculate total paid amount
  const totalPaid = allocations?.reduce((sum, allocation) => {
    const amount = typeof allocation.allocated_amount === 'string' 
      ? parseFloat(allocation.allocated_amount) 
      : allocation.allocated_amount;
    return sum + amount;
  }, 0) || 0;
  
  const paidPercentage = totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0;

  // Handle navigation to payment detail in new tab
  const handleNavigateToPayment = (paymentId: number) => {
    window.open(`/payments-in/detail/${paymentId}`, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Payment History - {invoiceNumber}
          </DialogTitle>
        </DialogHeader>

        {/* Invoice Summary Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Invoice Amount</p>
                <p className="text-2xl font-semibold mt-1">{formatCurrency(totalAmount)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Paid</p>
                <p className="text-2xl font-semibold text-success mt-1">{formatCurrency(totalPaid)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Balance Due</p>
                <p className="text-2xl font-semibold text-warning mt-1">{formatCurrency(amountDue)}</p>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex justify-between text-xs font-medium mb-2">
                <span className="text-muted-foreground uppercase tracking-wide">Payment Progress</span>
                <span>{paidPercentage.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2">
                <div 
                  className="bg-success h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(paidPercentage, 100)}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Allocations Table */}
        <div className="mt-6">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4">
            Payment Receipts
          </h3>

          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Failed to load payment allocations</p>
              <p className="text-sm mt-2">{error instanceof Error ? error.message : 'Unknown error'}</p>
            </div>
          ) : allocations && allocations.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Receipt No.</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Customer</th>
                    <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Payment Date</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Receipt Amount</th>
                    <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Allocated Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((allocation, index) => {
                    const receiptAmount = allocation.payment_voucher?.total_amount || 0;
                    const allocatedAmount = allocation.allocated_amount;
                    const isPartialAllocation = parseFloat(String(receiptAmount)) !== parseFloat(String(allocatedAmount));
                    
                    return (
                      <tr key={allocation.id} className="border-b last:border-b-0 hover:bg-muted/50 transition-colors">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <a
                              href={`/payments-in/detail/${allocation.payment_voucher_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-primary hover:underline"
                              onClick={(e) => {
                                e.preventDefault();
                                handleNavigateToPayment(allocation.payment_voucher_id);
                              }}
                            >
                              {allocation.payment_voucher?.voucher_number || `Payment #${allocation.payment_voucher_id}`}
                            </a>
                            {isPartialAllocation && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge variant="outline" className="text-xs">Partial</Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>This receipt was partially allocated to this invoice</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-sm">
                            {allocation.payment_voucher?.party_name || 'N/A'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-sm">{formatDate(allocation.payment_voucher?.transaction_date || allocation.allocation_date)}</span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="text-sm text-muted-foreground">
                            {formatCurrency(receiptAmount)}
                          </span>
                        </td>
                        <td className="p-3 text-right font-semibold">
                          {formatCurrency(allocatedAmount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t bg-muted/50">
                    <td colSpan={3} className="p-3 text-sm font-semibold">Total Payments Received</td>
                    <td className="p-3 text-right"></td>
                    <td className="p-3 text-right text-sm font-semibold text-success">
                      {formatCurrency(totalPaid)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>No payments received yet</p>
              <p className="text-sm mt-2">This invoice has not received any payments</p>
            </div>
          )}
        </div>

        {/* Additional Information */}
        {allocations && allocations.length > 0 && (
          <div className="mt-6 p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">Note:</span> Click on receipt number to view payment details in a new tab. 
              {allocations.some(a => parseFloat(String(a.payment_voucher?.total_amount || 0)) !== parseFloat(String(a.allocated_amount))) && 
                ' Some receipts show partial allocation, meaning the payment was split across multiple invoices.'}
            </p>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};